import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * A short-lived "saved" flag for in-place save feedback (FE-ADR-065): call
 * `flash()` when a save succeeds and `saved` is true for `ms`, so the form that
 * stays on screen can say "Saved" where the user is looking instead of a toast.
 * A second save restarts the timer; `clear()` drops it early — call it when the
 * form is edited again, so "Saved" never sits next to an unsaved change.
 */
export function useSavedFlash(ms = 2000) {
  const [saved, setSaved] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const flash = useCallback(() => {
    clearTimeout(timer.current);
    setSaved(true);
    timer.current = setTimeout(() => setSaved(false), ms);
  }, [ms]);

  const clear = useCallback(() => {
    clearTimeout(timer.current);
    setSaved(false);
  }, []);

  return { saved, flash, clear };
}
